import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './CreateScrim.module.css';
import { createScrim, createScrimTracks, getYoutubeInfo, launchScrim } from '../utils/api';

const CreateScrim: React.FC = () => {
  const navigate = useNavigate();
  const [urls, setUrls] = useState('');
  const [scrimName, setScrimName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState('');

  // Random example YouTube URLs for placeholder
  const exampleUrls = [
    'https://www.youtube.com/watch?v=xK4J57Nq8wY',
    'https://www.youtube.com/live/pL9V22Kx7Hw',
    'https://youtu.be/mR5X24tP2Zk'
  ];

  const extractYouTubeId = (url: string): string | null => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/live\/)([^&\n?#]+)/,
      /^([a-zA-Z0-9_-]{11})$/ // Direct video ID
    ];
    
    for (const pattern of patterns) {
      const match = url.trim().match(pattern);
      if (match) return match[1];
    }
    
    return null;
  };

  const validateUrls = (urlText: string): string[] => {
    if (!urlText.trim()) return [];
    
    const lines = urlText.split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);
    
    return lines;
  };

  const handleCreate = async () => {
    setError('');
    setIsCreating(true);
    
    try {
      // Validate input
      if (!scrimName.trim()) {
        setError('Please enter a scrim name');
        return;
      }

      const urlLines = validateUrls(urls);
      if (urlLines.length === 0) {
        setError('Please enter at least one YouTube URL');
        return;
      }

      setProgress('Validating YouTube URLs...');

      // Extract and validate YouTube IDs (silently deduplicate)
      const invalidUrls: string[] = [];
      const seenIds = new Set<string>();
      const uniqueVideos: { youtubeId: string; originalUrl: string; lineNumber: number }[] = [];

      for (let i = 0; i < urlLines.length; i++) {
        const url = urlLines[i];
        const youtubeId = extractYouTubeId(url);
        
        if (!youtubeId) {
          invalidUrls.push(`Line ${i + 1}: "${url}" - Invalid YouTube URL format`);
          continue;
        }

        // Silently skip duplicates
        if (seenIds.has(youtubeId)) {
          continue;
        }
        seenIds.add(youtubeId);
        uniqueVideos.push({ youtubeId, originalUrl: url, lineNumber: i + 1 });
      }

      // Enforce maximum unique URLs allowed
      if (uniqueVideos.length > 6) {
        setError('Maximum 6 URLs allowed');
        return;
      }

      if (invalidUrls.length > 0) {
        setError(`Invalid URLs found:\n${invalidUrls.join('\n')}`);
        return;
      }

      // Validate each YouTube ID with our API and collect video info
      setProgress('Checking video availability...');
      const validationErrors: string[] = [];
      const videoInfos: any[] = [];

      for (let i = 0; i < uniqueVideos.length; i++) {
        const { youtubeId, originalUrl, lineNumber } = uniqueVideos[i];
        try {
          const videoInfo = await getYoutubeInfo(youtubeId);
          videoInfos.push({ ...videoInfo, lineNumber, originalUrl });
        } catch (err) {
          validationErrors.push(`Line ${lineNumber}: "${originalUrl}" - Video not found or unavailable`);
        }
      }

      if (validationErrors.length > 0) {
        setError(`Video validation failed:\n${validationErrors.join('\n')}`);
        return;
      }

      // Check that all videos are streams with start times
      setProgress('Validating stream times...');
      const streamStartTimes: Date[] = [];
      const videoTimeErrors: string[] = [];

      for (const videoInfo of videoInfos) {
        if (!videoInfo.stream_start_utc) {
          videoTimeErrors.push(`Line ${videoInfo.lineNumber}: "${videoInfo.originalUrl}" - This video is not a streamed video. Only streamed videos can be synced as scrim tracks.`);
          continue;
        }

        try {
          const streamStart = new Date(videoInfo.stream_start_utc);
          if (isNaN(streamStart.getTime())) {
            videoTimeErrors.push(`Line ${videoInfo.lineNumber}: "${videoInfo.originalUrl}" - Invalid stream start time`);
            continue;
          }
          streamStartTimes.push(streamStart);
        } catch (err) {
          videoTimeErrors.push(`Line ${videoInfo.lineNumber}: "${videoInfo.originalUrl}" - Invalid stream start time format`);
        }
      }

      if (videoTimeErrors.length > 0) {
        setError(`Stream time validation failed:\n${videoTimeErrors.join('\n')}`);
        return;
      }

      // Check if all streams started within 6 hours of each other
      if (streamStartTimes.length > 1) {
        const sortedTimes = [...streamStartTimes].sort((a, b) => a.getTime() - b.getTime());
        const earliestTime = sortedTimes[0];
        const latestTime = sortedTimes[sortedTimes.length - 1];
        const timeDifferenceHours = (latestTime.getTime() - earliestTime.getTime()) / (1000 * 60 * 60);

        if (timeDifferenceHours > 6) {
          const formatTime = (date: Date) => date.toISOString().replace('T', ' ').replace('Z', ' UTC');
          setError(
            `Youtube video stream time validation failed:\n` +
            `Youtube streams must have started within 6 hours of each other.\n` +
            `Earliest stream: ${formatTime(earliestTime)}\n` +
            `Latest stream: ${formatTime(latestTime)}\n` +
            `Time difference: ${timeDifferenceHours.toFixed(1)} hours`
          );
          return;
        }
      }

      // Create the scrim
      setProgress('Creating scrim...');
      const scrimResponse = await createScrim(scrimName.trim());
      const scrimId = scrimResponse.scrim_id;

      // Create all tracks in a single API call
      setProgress('Adding tracks...');
      const tracksToCreate = uniqueVideos.map(({ youtubeId }, index) => ({
        youtube_id: youtubeId,
        track_index: index + 1
      }));
      
      await createScrimTracks(scrimId, tracksToCreate);

      // Trigger the launcher once after all tracks are created
      setProgress('Launching processing...');
      await launchScrim(scrimId);

      // Navigate to the created scrim replay page
      navigate(`/scrim/${scrimId}/replay`);

    } catch (err) {
      console.error('Error creating scrim:', err);
      setError(err instanceof Error ? err.message : 'Failed to create scrim');
    } finally {
      setIsCreating(false);
      setProgress('');
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button 
          className={styles.backButton}
          onClick={() => navigate('/scrims')}
        >
          <i className="bi bi-arrow-left"></i>
          Back to Scrims
        </button>
        
        <h1>Create Scrim</h1>
        <p className={styles.description}>
          Paste the YouTube URLs from your scrim session below.<br />We'll process and auto-sync them together.
        </p>
      </div>

      <div className={styles.form}>
        <div className={styles.inputGroup}>
          <label htmlFor="scrimName">Scrim Name</label>
          <input
            id="scrimName"
            type="text"
            autoFocus
            value={scrimName}
            onChange={(e) => setScrimName(e.target.value)}
            placeholder="Enter scrim name..."
            className={styles.nameInput}
            disabled={isCreating}
          />
        </div>

        <div className={styles.inputGroup}>
          <label htmlFor="urls">YouTube URLs</label>
          <p className={styles.inputDescription}>
            Paste one or more YouTube URLs, each on a separate line. Maximum 6 URLs.
          </p>
          <textarea
            id="urls"
            value={urls}
            onChange={(e) => setUrls(e.target.value)}
            placeholder={exampleUrls.join('\n')}
            rows={6}
            className={styles.urlsTextarea}
            disabled={isCreating}
          />
        </div>

        {error && (
          <div className={styles.error}>
            <i className="bi bi-exclamation-triangle"></i>
            <pre>{error}</pre>
          </div>
        )}

        {isCreating && (
          <div className={styles.progress}>
            <div className={styles.spinner}></div>
            <span>{progress}</span>
          </div>
        )}

        <div className={styles.actions}>
          <button
            className={styles.createButton}
            onClick={handleCreate}
            disabled={isCreating}
          >
            {isCreating ? 'Creating...' : 'Create Scrim'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateScrim; 